import { useMemo, useState, useEffect } from 'react';
import { formatINR } from '@/utils/format';
import { complianceApi, type TDSEntryRecord, type TDSSectionRecord, type GstReturnFiling, type TdsReceivableSummary } from '@/api/compliance.api';

interface DashboardTabProps {
  entries?: TDSEntryRecord[];
  sections?: TDSSectionRecord[];
  onDepositNow?: (entryId: string) => void;
  /** Switches the parent page to the "TDS Receivable" tab */
  onViewReceivable?: () => void;
}

function currentRetPeriod(): string {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
}

export default function DashboardTab({ entries, sections, onDepositNow, onViewReceivable }: DashboardTabProps) {
  const activeEntries = entries || [];
  const activeSections = sections || [];

  const now = new Date();

  // ─── GSTR7 / GSTR8 filing status for the current period ──────────
  const [gstr7Filing, setGstr7Filing] = useState<GstReturnFiling | null>(null);
  const [gstr8Filing, setGstr8Filing] = useState<GstReturnFiling | null>(null);
  const [loadingFilings, setLoadingFilings] = useState(true);
  const period = currentRetPeriod();

  useEffect(() => {
    (async () => {
      setLoadingFilings(true);
      try {
        const [g7, g8] = await Promise.all([
          complianceApi.getGstFilings('GSTR7', period).catch(() => null),
          complianceApi.getGstFilings('GSTR8', period).catch(() => null),
        ]);
        if (g7?.success) setGstr7Filing((g7.data as GstReturnFiling) || null);
        if (g8?.success) setGstr8Filing((g8.data as GstReturnFiling) || null);
      } finally {
        setLoadingFilings(false);
      }
    })();
  }, [period]);

  // ─── TDS Receivable (customers deducting from us — Stage 3/4) — shown here so it
  // never reads as "the money vanished" next to the vendor-side cards below, which
  // are a completely different flow (TDS we deduct from our own vendors). ──
  const [receivableSummary, setReceivableSummary] = useState<TdsReceivableSummary | null>(null);
  useEffect(() => {
    complianceApi.getTdsReceivableSummary().then((res) => {
      if (res?.success) setReceivableSummary(res.data || null);
    }).catch(() => {});
  }, []);

  // "Remind before Deposit Due Date" from TDS/TCS Settings drives the Due Soon window
  const [remindDays, setRemindDays] = useState(3);
  useEffect(() => {
    complianceApi.getTdsTcsSettings().then((res) => {
      if (res?.success && res.data) setRemindDays(Number(res.data.remindDays) || 3);
    }).catch(() => {});
  }, []);

  // ─── SECTION 1: This Quarter Overview ─────────────────────────────
  const deductedQtr = activeEntries
    .filter(e => e.type === 'TDS' && e.status === 'DEDUCTED')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  const depositedQtr = activeEntries
    .filter(e => e.type === 'TDS' && e.status === 'DEPOSITED')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  const tcsCollectedQtr = activeEntries
    .filter(e => e.type === 'TCS')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  const pendingDeposit = activeEntries
    .filter(e => e.status === 'DEDUCTED')
    .reduce((s, e) => s + Number(e.tdsAmount || 0), 0);

  // ─── SECTION 2: Section-wise Breakdown ────────────────────────────
  const sectionBreakdown = useMemo(() => {
    return activeSections
      .filter(s => activeEntries.some(e => e.section === s.section))
      .map(section => {
        const matching = activeEntries.filter(e => e.section === section.section);
        const total = matching.reduce((sum, e) => sum + Number(e.tdsAmount || 0), 0);
        return { section: section.section, description: section.description, total, matching };
      })
      .sort((a, b) => b.total - a.total);
  }, [activeEntries, activeSections]);

  const maxSectionTotal = Math.max(...sectionBreakdown.map(s => s.total), 1);

  // ─── SECTION 3: Top Deductees ─────────────────────────────────────
  const topDeductees = useMemo(() => {
    const grouped: Record<string, { key: string; partyName: string; partyPAN: string; section: string; total: number; status: string }> = {};
    activeEntries.forEach((e, idx) => {
      const key = e.partyId || e.partyPAN || e.partyName || `deductee-${idx}`;
      if (!grouped[key]) {
        grouped[key] = { key, partyName: e.partyName, partyPAN: e.partyPAN, section: e.section, total: 0, status: e.status };
      }
      grouped[key].total += Number(e.tdsAmount || 0);
      if (e.status === 'DEDUCTED') grouped[key].status = 'DEDUCTED';
      else if (grouped[key].status !== 'DEDUCTED' && e.status === 'DEPOSITED') grouped[key].status = 'DEPOSITED';
    });
    return Object.values(grouped)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [activeEntries]);

  // ─── SECTION 4: Deposit Due Calendar ──────────────────────────────
  const dueEntries = activeEntries.filter(e => e.status === 'DEDUCTED');

  // ─── SECTION 5: Annual PAN-wise Summary ───────────────────────────
  const panWiseSummary = useMemo(() => {
    const grouped: Record<string, { key: string; pan: string; partyName: string; totalPaid: number; totalTDS: number; sections: Set<string>; certCount: number }> = {};
    activeEntries.forEach((e, idx) => {
      const key = e.partyPAN || e.partyName || e.partyId || `pan-${idx}`;
      if (!grouped[key]) {
        grouped[key] = { key, pan: e.partyPAN, partyName: e.partyName, totalPaid: 0, totalTDS: 0, sections: new Set(), certCount: 0 };
      }
      grouped[key].totalPaid += Number(e.paymentAmount || 0);
      grouped[key].totalTDS += Number(e.tdsAmount || 0);
      grouped[key].sections.add(e.section);
      // 'DEPOSITED' only applies to vendor-TDS (type='TDS') entries — a TDS_RECEIVABLE
      // entry (customer deducted from us) has a certificate once certificateNo is set,
      // regardless of its own status value (CERTIFICATE_PENDING/RECEIVED/RECONCILED/CLAIMED).
      if (e.status === 'DEPOSITED' || e.certificateNo) grouped[key].certCount++;
    });
    return Object.values(grouped).map(g => ({
      ...g,
      sections: Array.from(g.sections),
    }));
  }, [activeEntries]);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      DEPOSITED: 'bg-green-100 text-green-700',
      DEDUCTED: 'bg-blue-100 text-blue-700',
      PENDING: 'bg-amber-100 text-amber-700',
      EXEMPTED: 'bg-gray-100 text-gray-500',
    };
    return `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map[status] || 'bg-gray-100 text-gray-500'}`;
  };

  const filingBadge = (filing: GstReturnFiling | null) => {
    if (!filing) return { cls: 'bg-gray-100 text-gray-500', label: 'Not started', icon: 'ri-time-line' };
    const map: Record<string, { cls: string; label: string; icon: string }> = {
      FILED: { cls: 'bg-green-100 text-green-700', label: 'Filed', icon: 'ri-checkbox-circle-fill' },
      ACCEPTED: { cls: 'bg-emerald-100 text-emerald-700', label: 'Accepted — ready to file', icon: 'ri-check-line' },
      SAVED: { cls: 'bg-blue-100 text-blue-700', label: 'Saved', icon: 'ri-save-line' },
      PROCESSING: { cls: 'bg-amber-100 text-amber-700', label: 'Processing', icon: 'ri-loader-4-line' },
      ERROR: { cls: 'bg-red-100 text-red-700', label: 'Error', icon: 'ri-error-warning-line' },
      DRAFT: { cls: 'bg-gray-100 text-gray-500', label: 'Draft', icon: 'ri-file-line' },
    };
    return map[filing.status] || { cls: 'bg-gray-100 text-gray-500', label: filing.status, icon: 'ri-question-line' };
  };

  return (
    <div className="space-y-6">
      {/* SECTION 0: GSTR7 / GSTR8 Filing Status (this period) */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
          Filing Status — {period.slice(0, 2)}/{period.slice(2)}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { title: 'GSTR7 (TDS Return)', filing: gstr7Filing },
            { title: 'GSTR8 (TCS Return)', filing: gstr8Filing },
          ].map(({ title, filing }) => {
            const b = filingBadge(filing);
            return (
              <div key={title} className="bg-white border border-[#e2e8f0] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-[#1e293b]">{title}</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${b.cls}`}>
                    <i className={b.icon} /> {loadingFilings ? 'Loading...' : b.label}
                  </span>
                </div>
                {filing?.arn ? (
                  <p className="text-xs text-slate-500">ARN: <span className="font-mono font-medium text-[#1e293b]">{filing.arn}</span></p>
                ) : filing?.ref_id ? (
                  <p className="text-xs text-slate-500">Ref ID: <span className="font-mono font-medium text-[#1e293b]">{filing.ref_id}</span></p>
                ) : !loadingFilings ? (
                  <p className="text-xs text-slate-400">Not yet saved for this period — use Quarterly Return to file.</p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* SECTION 1: This Quarter Overview — TDS/TCS WE deduct/collect from our own
          vendors and buyers. Separate from TDS Receivable below (money others deduct
          from payments TO us) — the two never share a total, by design. */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">This Quarter Overview — Vendor TDS / Buyer TCS</h3>
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'TDS Deducted (from Vendors)', value: deductedQtr, color: 'text-blue-600', bg: 'bg-blue-50', icon: 'ri-download-line' },
            { label: 'TDS Deposited', value: depositedQtr, color: 'text-green-600', bg: 'bg-green-50', icon: 'ri-check-double-line' },
            { label: 'TCS Collected (from Buyers)', value: tcsCollectedQtr, color: 'text-teal-600', bg: 'bg-teal-50', icon: 'ri-upload-line' },
            { label: 'Pending Deposit', value: pendingDeposit, color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-time-line' },
          ].map(card => (
            <div key={card.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.bg}`}>
                <i className={`${card.icon} ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-400">{card.label}</p>
                <p className={`text-lg font-bold ${card.color}`}>{formatINR(card.value)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 1b: TDS Receivable — customers (like Metro) deducting TDS from what
          they owe US. Lives in a separate table (type='TDS_RECEIVABLE') from the
          vendor-side cards above on purpose — see Stage 3/4. */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">TDS Receivable — Deducted by Customers From Us</h3>
          {onViewReceivable && (
            <button onClick={onViewReceivable} className="text-xs text-[#4f46e5] font-medium cursor-pointer hover:underline whitespace-nowrap">
              View all →
            </button>
          )}
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'FY Total TDS Receivable', value: receivableSummary?.fyTotalTds ?? 0, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: 'ri-wallet-3-line' },
            { label: 'Certificate Pending', value: receivableSummary?.certPendingAmount ?? 0, color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-time-line', suffix: receivableSummary ? ` (${receivableSummary.certPendingCount})` : '' },
            { label: 'Received', value: receivableSummary?.receivedAmount ?? 0, color: 'text-blue-600', bg: 'bg-blue-50', icon: 'ri-checkbox-circle-line' },
          ].map(card => (
            <div key={card.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.bg}`}>
                <i className={`${card.icon} ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-400">{card.label}</p>
                <p className={`text-lg font-bold ${card.color}`}>{formatINR(card.value)}{card.suffix || ''}</p>
              </div>
            </div>
          ))}
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
              <i className="ri-calendar-event-line text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Next Cert Due</p>
              <p className="text-sm font-bold text-[#1e293b]">
                {receivableSummary?.nextDue
                  ? new Date(receivableSummary.nextDue.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: Section-wise Breakdown */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Section-wise Breakdown</h3>
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 space-y-3">
          {sectionBreakdown.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No TDS entries yet</p>
          ) : (
            sectionBreakdown.map(s => {
              const pct = Math.round((s.total / maxSectionTotal) * 100);
              return (
                <div key={s.section} className="flex items-center gap-3">
                  <span className="text-xs font-mono font-semibold text-slate-600 w-12 shrink-0">{s.section}</span>
                  <div className="flex-1 h-5 bg-[#f1f5f9] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#4f46e5] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(pct, 5)}%` }}
                    >
                      <span className="text-[10px] font-semibold text-white">{formatINR(s.total)}</span>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 w-28 shrink-0 truncate">{s.description}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* SECTION 3: Top Deductees Table */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Top Deductees</h3>
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
                <th className="py-3 px-4 font-medium">Party</th>
                <th className="py-3 px-4 font-medium">PAN</th>
                <th className="py-3 px-4 font-medium">Section</th>
                <th className="py-3 px-4 font-medium text-right">Total Deducted</th>
                <th className="py-3 px-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {topDeductees.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-400 text-sm">No deductees found</td>
                </tr>
              ) : (
                topDeductees.map((d, idx) => (
                  <tr key={d.key || d.partyPAN || d.partyName || `deductee-${idx}`} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
                    <td className="py-3 px-4 font-medium text-[#1e293b]">{d.partyName}</td>
                    <td className="py-3 px-4 font-mono text-xs text-slate-500">{d.partyPAN || '—'}</td>
                    <td className="py-3 px-4 text-xs font-medium text-slate-600">{d.section}</td>
                    <td className="py-3 px-4 text-right font-bold text-red-600">{formatINR(d.total)}</td>
                    <td className="py-3 px-4"><span className={statusBadge(d.status)}>{d.status === 'DEDUCTED' ? 'Deducted' : d.status === 'DEPOSITED' ? 'Deposited' : d.status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION 4: Deposit Due Calendar */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Deposit Due Calendar</h3>
        <div className="space-y-3">
          {dueEntries.length === 0 ? (
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 text-sm text-slate-400 text-center">
              <i className="ri-check-double-line text-green-500 text-lg block mb-1" />
              All TDS deposited — nothing due!
            </div>
          ) : (
            dueEntries.map((entry, idx) => {
              const paymentDate = new Date(entry.paymentDate);
              const dueDate = new Date(paymentDate.getFullYear(), paymentDate.getMonth() + 1, 7);
              const isOverdue = now > dueDate;
              const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= remindDays;

              return (
                <div key={entry.id || `due-${idx}`}
                  className={`bg-white border rounded-xl p-4 flex items-center justify-between ${isOverdue ? 'border-red-300 bg-red-50/30' : isDueSoon ? 'border-amber-300 bg-amber-50/30' : 'border-[#e2e8f0]'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOverdue ? 'bg-red-100' : isDueSoon ? 'bg-amber-100' : 'bg-blue-50'
                      }`}>
                      <i className={`${isOverdue ? 'ri-error-warning-line text-red-600' : isDueSoon ? 'ri-alert-line text-amber-600' : 'ri-calendar-check-line text-blue-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1e293b]">
                        {isOverdue ? '⚠️ Overdue!' : isDueSoon ? '⚠️ Due Soon' : 'TDS Deposit Due'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatINR(entry.tdsAmount)} ({entry.section} — {entry.partyName})
                      </p>
                      <p className="text-xs text-slate-400">
                        Due: {dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {isOverdue && <span className="text-red-500 ml-1">({Math.abs(daysUntilDue)} days overdue)</span>}
                        {isDueSoon && !isOverdue && <span className="text-amber-600 ml-1">({daysUntilDue} days left)</span>}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onDepositNow?.(entry.id)}
                    className={`h-8 px-4 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${isOverdue ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-[#4f46e5] text-white hover:bg-indigo-700'
                      }`}
                  >
                    Deposit Now
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* SECTION 5: Annual PAN-wise Summary */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Annual PAN-wise Summary (26AS Reconciliation)</h3>
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
                <th className="py-3 px-4 font-medium">PAN</th>
                <th className="py-3 px-4 font-medium">Party Name</th>
                <th className="py-3 px-4 font-medium text-right">Total Paid</th>
                <th className="py-3 px-4 font-medium text-right">Total TDS</th>
                <th className="py-3 px-4 font-medium">Sections Used</th>
                <th className="py-3 px-4 font-medium text-center">Certificates Issued</th>
              </tr>
            </thead>
            <tbody>
              {panWiseSummary.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-400 text-sm">No PAN-wise data available</td>
                </tr>
              ) : (
                panWiseSummary.map((p, idx) => (
                  <tr key={p.key || p.pan || p.partyName || `pan-${idx}`} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
                    <td className="py-3 px-4 font-mono text-xs font-medium text-[#1e293b]">{p.pan || '—'}</td>
                    <td className="py-3 px-4 text-[#1e293b]">{p.partyName}</td>
                    <td className="py-3 px-4 text-right font-medium text-[#1e293b]">{formatINR(p.totalPaid)}</td>
                    <td className="py-3 px-4 text-right font-bold text-red-600">{formatINR(p.totalTDS)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {p.sections.map((s, sIdx) => (
                          <span key={s || sIdx} className="px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-indigo-50 text-indigo-700">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center font-medium text-green-600">
                      {p.certCount > 0 ? (
                        <span className="flex items-center justify-center gap-1"><i className="ri-file-text-line text-xs" /> {p.certCount}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}